/* eslint-disable react/forbid-prop-types */
import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, TextField, InputAdornment, Stack, IconButton,
    alpha, Menu, MenuItem, Tooltip, ListSubheader, Autocomplete,
    Checkbox, Badge, CircularProgress,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import { SYSTEM_FORM_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';
import FormQuickPreviewDrawer from './FormQuickPreviewDrawer';

// ── Only "medical/patient-facing" form types are allowed in this picker.
// CALL_SCRIPT, TCO, and any other non-patient form types are excluded.
const VALID_FORM_TYPES = SYSTEM_FORM_TYPES.map((t) => t.value);

// Map for short, picker-friendly labels (matches Subhadeep's mock copy).
const TYPE_LABEL_OVERRIDES = {
    CONSENT: 'Consent',
    MEDICAL_HISTORY: 'Medical',
    PATIENT_INTAKE: 'Intake',
    SURVEY: 'Smile Assess',
    TREATMENT_PLAN: 'Treatment Plan',
    REFERRAL: 'Referral',
    OTHER: 'Other',
};

const getTypeLabel = (formType) => (
    TYPE_LABEL_OVERRIDES[formType]
    || SYSTEM_FORM_TYPES.find((t) => t.value === formType)?.label
    || (formType || '').replace(/_/g, ' ')
);

const TYPE_COLORS = {
    CONSENT: '#078DEE',
    MEDICAL_HISTORY: '#FF5630',
    SURVEY: '#855CF8',
    PATIENT_INTAKE: '#00AB55',
    REFERRAL: '#FFAB00',
    TREATMENT_PLAN: '#FF3030',
    OTHER: '#637381',
};

/**
 * Step 1 — Pick the forms.
 *
 *   V1-style: compact type Select (with counts, hides empty buckets) + a
 *   single grouped Autocomplete dropdown for picking the form. Selected
 *   forms render as chips below; selecting 2+ reveals the sequence list
 *   with drag-to-reorder.
 *
 *   Each option exposes a small "Preview" affordance that opens the
 *   FormQuickPreviewDrawer — the user can inspect the rendered form
 *   without leaving the wizard.
 */
const QuickSendStep1Forms = ({
    forms,
    formsLoading,
    selectedForms,
    onAdd,
    onRemove,
    onReorder,
    practiceId,
}) => {
    const [activeType, setActiveType] = useState('ALL');
    const [previewForm, setPreviewForm] = useState(null);
    // Type-filter trigger is now an icon-only button; the menu anchors to it.
    const filterAnchorRef = useRef(null);
    const [filterOpen, setFilterOpen] = useState(false);

    // ── Derive valid forms — drop CALL_SCRIPT / TCO and any non-system type.
    const formsList = useMemo(() => {
        const raw = Array.isArray(forms) ? forms : (forms?.results || []);
        return raw
            .filter(Boolean)
            .filter((f) => VALID_FORM_TYPES.includes(f.form_type));
    }, [forms]);

    // ── Counts per type — used to (a) show n in the dropdown, (b) hide
    // types that have zero forms.
    const typeCounts = useMemo(() => {
        const counts = {};
        formsList.forEach((f) => {
            counts[f.form_type] = (counts[f.form_type] || 0) + 1;
        });
        return counts;
    }, [formsList]);

    const visibleTypes = useMemo(() => (
        SYSTEM_FORM_TYPES.filter((t) => (typeCounts[t.value] || 0) > 0)
    ), [typeCounts]);

    // ── Filter + sort: by type then alphabetical within group.
    const filteredForms = useMemo(() => {
        let list = formsList;
        if (activeType !== 'ALL') {
            list = list.filter((f) => f.form_type === activeType);
        }
        return list.slice().sort((a, b) => {
            // Sort by form_type (stable order from SYSTEM_FORM_TYPES) first,
            // then alphabetical by name within the group.
            const ai = SYSTEM_FORM_TYPES.findIndex((t) => t.value === a.form_type);
            const bi = SYSTEM_FORM_TYPES.findIndex((t) => t.value === b.form_type);
            if (ai !== bi) return ai - bi;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [formsList, activeType]);

    const selectedIds = useMemo(
        () => new Set(selectedForms.map((f) => f.id)),
        [selectedForms],
    );

    // Diff the previous selection against the picker's new value. Anything
    // newly checked goes through onAdd (preserves order), anything unchecked
    // is dropped via onRemove. We rely on the parent's onAdd to no-op on
    // duplicates and on onRemove to no-op on missing ids.
    const handleMultiChange = (_, newValue) => {
        const nextIds = new Set(newValue.map((f) => f.id));
        // Removed: in selectedIds but not in nextIds
        selectedForms.forEach((f) => {
            if (!nextIds.has(f.id)) onRemove(f.id);
        });
        // Added: in newValue but not in current selectedIds
        newValue.forEach((f) => {
            if (!selectedIds.has(f.id)) onAdd(f);
        });
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        onReorder(result.source.index, result.destination.index);
    };

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em',
                    color: common.mintyZest, textTransform: 'uppercase', mb: 0.5,
                }}
            >
                Step 1 · Forms
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5 }}>
                Pick forms to send
            </Typography>

            {/* Control row: icon-only Filter button + Form picker.
                Filter button shows a badge with active filter (or total count)
                so users see at a glance how the picker is scoped, without
                burning real estate on a text Select that competed with the
                actual form picker. */}
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.25 }}>
                <Tooltip title={activeType === 'ALL' ? 'Filter by type' : `Filter: ${getTypeLabel(activeType)} · Click to change`} enterDelay={300}>
                    <Badge
                        badgeContent={
                            activeType === 'ALL'
                                ? formsList.length
                                : (typeCounts[activeType] || 0)
                        }
                        color="default"
                        max={99}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        sx={{
                            '& .MuiBadge-badge': {
                                top: 4, right: 4,
                                fontSize: '9px', fontWeight: 800, height: 14, minWidth: 14, p: '0 4px',
                                bgcolor: activeType === 'ALL' ? 'rgba(145,158,171,0.18)' : alpha(common.mintyZest, 0.18),
                                color: activeType === 'ALL' ? 'text.secondary' : common.mintyZest,
                            },
                        }}
                    >
                        <IconButton
                            ref={filterAnchorRef}
                            onClick={() => setFilterOpen(true)}
                            aria-label="Filter forms by type"
                            sx={{
                                width: 40, height: 40, borderRadius: '10px',
                                border: '1px solid',
                                borderColor: activeType === 'ALL'
                                    ? 'rgba(145,158,171,0.32)'
                                    : alpha(common.mintyZest, 0.5),
                                bgcolor: activeType === 'ALL' ? '#fff' : alpha(common.mintyZest, 0.06),
                                color: activeType === 'ALL' ? 'text.secondary' : common.mintyZest,
                                '&:hover': {
                                    borderColor: common.mintyZest,
                                    bgcolor: alpha(common.mintyZest, 0.06),
                                },
                            }}
                        >
                            <Iconify icon="eva:funnel-outline" width={18} />
                        </IconButton>
                    </Badge>
                </Tooltip>
                <Menu
                    open={filterOpen}
                    anchorEl={filterAnchorRef.current}
                    onClose={() => setFilterOpen(false)}
                    PaperProps={{ sx: { maxHeight: 420, minWidth: 200, mt: 0.5 } }}
                >
                    <MenuItem
                        selected={activeType === 'ALL'}
                        onClick={() => { setActiveType('ALL'); setFilterOpen(false); }}
                        sx={{ fontSize: '13px', fontWeight: 600 }}
                    >
                        All types
                        <Typography component="span" sx={{ ml: 'auto', color: 'text.disabled', fontSize: '12px' }}>
                            {formsList.length}
                        </Typography>
                    </MenuItem>
                    {visibleTypes.map((t) => (
                        <MenuItem
                            key={t.value}
                            selected={activeType === t.value}
                            onClick={() => { setActiveType(t.value); setFilterOpen(false); }}
                            sx={{ fontSize: '13px' }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                    bgcolor: TYPE_COLORS[t.value] || '#637381',
                                    mr: 0.875, flexShrink: 0,
                                }}
                            />
                            {getTypeLabel(t.value)}
                            <Typography component="span" sx={{ ml: 'auto', color: 'text.disabled', fontSize: '12px' }}>
                                {typeCounts[t.value]}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>

                <Autocomplete
                    fullWidth
                    multiple
                    size="small"
                    loading={formsLoading}
                    disableCloseOnSelect
                    options={filteredForms}
                    value={selectedForms}
                    groupBy={(opt) => getTypeLabel(opt.form_type)}
                    getOptionLabel={(opt) => opt?.name || 'Untitled form'}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    onChange={handleMultiChange}
                    // Compact popper — three full rows visible, the
                    // rest scroll. ``mb`` on the listbox (NOT padding
                    // on the Paper, which MUI's internal styles can
                    // override) forces a clear 16px white band
                    // between the last row and the Paper's rounded
                    // bottom edge so the curve is unambiguously
                    // visible.
                    ListboxProps={{
                        sx: {
                            maxHeight: 188,
                            pt: 0.5,
                            pb: 0,
                            mt: 0,
                            mb: 2,
                            scrollbarGutter: 'stable',
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(145,158,171,0.32)',
                                borderRadius: 6,
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: 'rgba(145,158,171,0.48)',
                            },
                        },
                    }}
                    // Flip upward when the trigger sits low in the
                    // dialog so the dropdown stays inside the dialog
                    // bounds. ``preventOverflow`` keeps it from being
                    // nudged off-screen on tight viewports.
                    //
                    // CRITICAL: `src/App.css` ships a global rule
                    // `.MuiAutocomplete-popper { background: #fff }`
                    // which renders an unrounded white rectangle
                    // BEHIND the Paper — that's what was leaking
                    // past the rounded Paper at the bottom. We force
                    // the Popper's background back to transparent
                    // here (sx beats the global class selector on
                    // specificity) so only the Paper is visible.
                    slotProps={{
                        popper: {
                            sx: {
                                background: 'transparent !important',
                                backgroundColor: 'transparent !important',
                            },
                            modifiers: [
                                { name: 'flip', enabled: true },
                                { name: 'preventOverflow', enabled: true, options: { altAxis: true, padding: 8 } },
                            ],
                        },
                    }}
                    componentsProps={{
                        paper: {
                            sx: {
                                mt: 0.75,
                                // Strong 18px curve so the rounded
                                // bottom corners are immediately
                                // obvious, not subtle.
                                borderRadius: '18px',
                                boxShadow: '0 20px 48px rgba(0,0,0,0.16), 0 2px 10px rgba(0,0,0,0.05)',
                                border: '1px solid',
                                borderColor: 'rgba(145,158,171,0.2)',
                                overflow: 'hidden',
                                bgcolor: '#fff',
                                // Hard-override any MUI default padding
                                // on the inner listbox so our ``mb: 2``
                                // wins and the bottom white band is
                                // always present.
                                '& .MuiAutocomplete-listbox': {
                                    marginBottom: '16px !important',
                                },
                                // The last visible row's hairline
                                // borderBottom can otherwise pretend
                                // to be the dropdown's bottom edge —
                                // strip it so only the Paper's
                                // rounded curve reads as the end.
                                '& .MuiAutocomplete-listbox > li:last-of-type, & .MuiAutocomplete-listbox > li > ul > li:last-of-type': {
                                    borderBottom: 'none',
                                },
                            },
                        },
                    }}
                    // The selected forms are shown as a styled list under the
                    // picker (sequence/order matters there) — keep the input
                    // visually empty rather than rendering a row of chips.
                    renderTags={() => null}
                    noOptionsText={
                        formsList.length === 0
                            ? 'No medical forms found for this practice.'
                            : 'No forms match this filter.'
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={
                                selectedForms.length === 0
                                    ? 'Search & pick forms…'
                                    : `${selectedForms.length} selected — add more or close`
                            }
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" width={16} sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {formsLoading && (
                                            <CircularProgress size={14} sx={{ color: 'text.disabled', mr: 0.5 }} />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px', bgcolor: '#fff' },
                            }}
                        />
                    )}
                    renderGroup={(params) => (
                        <li key={params.key}>
                            <ListSubheader
                                disableSticky
                                sx={{
                                    fontSize: '10px', fontWeight: 800, letterSpacing: '0.06em',
                                    textTransform: 'uppercase', lineHeight: 1.8,
                                    bgcolor: 'rgba(145,158,171,0.06)', color: 'text.disabled',
                                    py: 0.25, px: 1.25,
                                }}
                            >
                                {params.group}
                            </ListSubheader>
                            <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
                        </li>
                    )}
                    renderOption={(props, opt, { selected }) => {
                        const sectionCount = Array.isArray(opt.sections) ? opt.sections.length : 0;
                        return (
                            <Box
                                component="li"
                                {...props}
                                key={opt.id}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    py: 0.75, px: 1.25, fontSize: '13px',
                                    borderBottom: '1px solid',
                                    borderColor: 'rgba(145,158,171,0.08)',
                                    transition: 'background-color 100ms ease',
                                    '&:last-of-type': { borderBottom: 'none' },
                                    '&.Mui-focused, &:hover': {
                                        bgcolor: `${alpha(common.mintyZest, 0.06)} !important`,
                                    },
                                    ...(selected && {
                                        bgcolor: `${alpha(common.mintyZest, 0.04)} !important`,
                                    }),
                                }}
                            >
                                <Checkbox
                                    checked={selected}
                                    size="small"
                                    tabIndex={-1}
                                    sx={{
                                        p: 0.5,
                                        color: 'rgba(145,158,171,0.5)',
                                        '&.Mui-checked': { color: common.mintyZest },
                                    }}
                                />
                                <Box
                                    sx={{
                                        width: 4, height: 28, borderRadius: 2, flexShrink: 0,
                                        bgcolor: TYPE_COLORS[opt.form_type] || '#637381',
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }} noWrap>
                                        {opt.name || 'Untitled form'}
                                    </Typography>
                                    {sectionCount > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.disabled', fontSize: '11px' }}
                                            noWrap
                                        >
                                            {sectionCount} section{sectionCount === 1 ? '' : 's'}
                                        </Typography>
                                    )}
                                </Box>
                                <Tooltip title="Preview form" enterDelay={300}>
                                    <IconButton
                                        size="small"
                                        onMouseDown={(e) => {
                                            // Prevent Autocomplete from toggling
                                            // the option when the preview icon
                                            // is clicked.
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewForm(opt);
                                        }}
                                        sx={{
                                            p: 0.5, color: 'text.disabled',
                                            '&:hover': { color: common.mintyZest, bgcolor: alpha(common.mintyZest, 0.06) },
                                        }}
                                        aria-label={`Preview ${opt.name}`}
                                    >
                                        <Iconify icon="eva:eye-outline" width={14} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    }}
                />
            </Stack>

            {/* Selected forms / sequence list */}
            {selectedForms.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {selectedForms.length >= 2 && (
                        <Box
                            sx={{
                                p: 1, mb: 0.75,
                                borderRadius: '8px',
                                bgcolor: alpha('#078DEE', 0.08),
                                border: '1px solid', borderColor: alpha('#078DEE', 0.16),
                                color: '#078DEE',
                                fontSize: '11px',
                                display: 'flex', alignItems: 'center', gap: 0.75,
                            }}
                        >
                            <Iconify icon="eva:info-fill" width={14} />
                            These forms will be sent as a sequence — patient completes them one after another.
                        </Box>
                    )}

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="quick-send-sequence" isDropDisabled={selectedForms.length < 2}>
                            {(provided) => (
                                <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                        p: 0.75, borderRadius: '10px',
                                        bgcolor: alpha(common.mintyZest, 0.06),
                                        border: '1px dashed', borderColor: alpha(common.mintyZest, 0.28),
                                        display: 'flex', flexDirection: 'column', gap: 0.5,
                                    }}
                                >
                                    {selectedForms.map((form, index) => (
                                        <Draggable
                                            key={String(form.id)}
                                            draggableId={String(form.id)}
                                            index={index}
                                            isDragDisabled={selectedForms.length < 2}
                                        >
                                            {(dragProvided, snapshot) => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        p: 0.75, borderRadius: '8px', bgcolor: '#fff',
                                                        border: '1px solid', borderColor: 'divider',
                                                        boxShadow: snapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
                                                        ...dragProvided.draggableProps.style,
                                                    }}
                                                >
                                                    {selectedForms.length >= 2 && (
                                                        <Box
                                                            {...dragProvided.dragHandleProps}
                                                            sx={{
                                                                cursor: 'grab', color: 'text.disabled',
                                                                display: 'flex', alignItems: 'center',
                                                            }}
                                                            aria-label="Drag to reorder"
                                                        >
                                                            <Iconify icon="eva:menu-2-fill" width={14} />
                                                        </Box>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            width: 20, height: 20, borderRadius: '50%',
                                                            bgcolor: common.mintyZest, color: '#fff',
                                                            fontSize: '10px', fontWeight: 800,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Box>
                                                    <Typography sx={{ flex: 1, fontSize: '12.5px', fontWeight: 600 }} noWrap>
                                                        {form.name}
                                                    </Typography>
                                                    <Tooltip title="Preview form" enterDelay={300}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setPreviewForm(form)}
                                                            sx={{
                                                                p: 0.5, color: 'text.disabled',
                                                                '&:hover': { color: common.mintyZest, bgcolor: alpha(common.mintyZest, 0.06) },
                                                            }}
                                                            aria-label={`Preview ${form.name}`}
                                                        >
                                                            <Iconify icon="eva:eye-outline" width={14} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onRemove(form.id)}
                                                        sx={{ p: 0.5, color: 'text.disabled' }}
                                                        aria-label={`Remove ${form.name}`}
                                                    >
                                                        <Iconify icon="eva:close-fill" width={14} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            )}

            {/* Quick preview drawer */}
            {previewForm && (
                <FormQuickPreviewDrawer
                    open
                    onClose={() => setPreviewForm(null)}
                    form={previewForm}
                    practiceId={practiceId}
                />
            )}
        </Box>
    );
};

QuickSendStep1Forms.propTypes = {
    forms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    formsLoading: PropTypes.bool,
    selectedForms: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onReorder: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

QuickSendStep1Forms.defaultProps = {
    forms: [],
    formsLoading: false,
    selectedForms: [],
    practiceId: null,
};

export default QuickSendStep1Forms;
